/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useEffect, useState } from 'react'
import { VariantsEditor } from './VariantsEditor.js'
import { IngredientsEditor } from './IngredientsEditor.js'
import { ExtrasEditor } from './ExtrasEditor.js'
import { ImagesEditor } from './ImagesEditor.js'
import { currencyFor, baseLocaleOf, displayTranslation, sourceTranslation } from './shared.js'
import type { useQueryClient } from '@tanstack/react-query'
import { useQuery, useMutation } from '@tanstack/react-query'
import { products as prodApi, type Category, type Product, kitchensApi } from '../../lib/api.js'
import { Button, Input, Textarea, Select, Modal, toast } from '../../components/ui/index.js'
import { parseCents } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'
import { AllergensEditor } from '../../components/AllergensEditor.js'
import { AttributesEditor } from '../../components/AttributesEditor.js'
import { DailySpecialEditor } from '../../components/DailySpecialEditor.js'
import { PromotionForm } from '../../components/PromotionForm.js'
// ─── ProductModal ─────────────────────────────────────────────────────────────

export function ProductModal({
  open,
  prod,
  focusPromo,
  focusPrice,
  categories,
  locale,
  currency,
  priceLocale = '',
  countryCode,
  timezone,
  defaultCategoryId,
  onClose,
  qc,
}: {
  open: boolean
  prod?: Product
  /** veio pelo atalho "%": rola até a seção Promoção ao abrir */
  focusPromo?: boolean
  /** #489 M1: veio do aviso «Corrigir preço» (prato a 0) — foca o campo de
   * preço ao abrir (mesmo padrão do focusPromo) */
  focusPrice?: boolean
  categories: Category[]
  locale: string
  currency: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  /** #428: país do tenant — início da semana no Prato do dia */
  countryCode?: string | null
  timezone: string | null
  defaultCategoryId?: string
  onClose: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t, lang } = useI18n()
  // código BASE do idioma principal (#403): gravações sem linha existente
  // usam a base ('pt'), nunca o código regional do config
  const baseLocale = baseLocaleOf(locale)
  // linha-fonte do primário (#387): cascata exato → mesma base (manual
  // primeiro) → nada. Nenhum campo nasce pré-preenchido com outra língua.
  const source = prod ? sourceTranslation(prod.translations, locale) : undefined
  const [name, setName] = useState(source?.name ?? '')
  const [desc, setDesc] = useState(source?.description ?? '')
  // locale da linha EFETIVAMENTE carregada — o indicador e o PUT acompanham
  // (gravar na linha lida); sem linha da base, grava no CÓDIGO BASE
  const [sourceLocale, setSourceLocale] = useState(source?.locale ?? baseLocale)
  const [priceStr, setPriceStr] = useState(
    prod ? String(prod.price_cents / 100).replace('.', ',') : '',
  )
  const [catId, setCatId] = useState(
    prod?.category_id ?? defaultCategoryId ?? categories[0]?.id ?? '',
  )
  const [taxCat, setTaxCat] = useState(prod?.tax_category ?? 'food')
  const [availDineIn, setAvailDineIn] = useState(prod?.available_dine_in ?? true)
  const [availTakeaway, setAvailTakeaway] = useState(prod?.available_takeaway ?? true)
  const [availDelivery, setAvailDelivery] = useState(prod?.available_delivery ?? true)
  const [variantLabel, setVariantLabel] = useState(prod?.variant_group_label ?? '')
  const [kitchenId, setKitchenId] = useState(prod?.kitchen_id ?? '')

  // cozinhas (estações de produção) — o select só aparece se existirem
  const { data: kitchenData } = useQuery({
    queryKey: ['kitchens'],
    queryFn: () => kitchensApi.list(),
    enabled: open,
  })
  const kitchens = kitchenData?.kitchens ?? []

  // indicador do idioma do texto (#403): SÓ o código base em minúsculas
  // (decisão do Eduardo no review — «apenas os termos como pt, en»)
  const languageLabel = baseLocaleOf(sourceLocale)

  // currentProd: undefined enquanto cria, preenchido após criação ou ao editar
  const [currentProd, setCurrentProd] = useState<Product | undefined>(prod)

  // Reset ao abrir
  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      const tr = prod ? sourceTranslation(prod.translations, locale) : undefined
      setSourceLocale(tr?.locale ?? baseLocale)
      setName(tr?.name ?? '')
      setDesc(tr?.description ?? '')
      setPriceStr(prod ? String(prod.price_cents / 100).replace('.', ',') : '')
      setCatId(prod?.category_id ?? defaultCategoryId ?? categories[0]?.id ?? '')
      setTaxCat(prod?.tax_category ?? 'food')
      setAvailDineIn(prod?.available_dine_in ?? true)
      setAvailTakeaway(prod?.available_takeaway ?? true)
      setAvailDelivery(prod?.available_delivery ?? true)
      setVariantLabel(prod?.variant_group_label ?? '')
      setKitchenId(prod?.kitchen_id ?? '')
      setCurrentProd(prod)
    }
  }

  // atalho "%" (#205): abre o editor já posicionado na seção Promoção
  useEffect(() => {
    if (!open || !focusPromo || !prod) return
    const el = document.querySelector(`[data-testid="catalog-promo-${prod.id}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- roda só ao abrir
  }, [open, focusPromo, prod?.id])

  // #489 M1 (item 3): «Corrigir preço» no aviso do «No ar» a 0 — o editor
  // abre com o foco no campo de preço (com variantes o campo é readOnly mas
  // segue focável; a correção é editar os preços das variantes)
  useEffect(() => {
    if (!open || !focusPrice || !prod) return
    const el = document.querySelector<HTMLInputElement>('[data-testid="catalog-product-price"]')
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- roda só ao abrir
  }, [open, focusPrice, prod?.id])

  // #479: com variantes o preço base do produto é DERIVADO (mínimo das
  // disponíveis, recalculado pela API) — o campo fica só de leitura,
  // atualizado pelas respostas das escritas de variante. Sem variantes, o
  // campo segue manual como sempre.
  const hasVariants = (currentProd?.variants?.length ?? 0) > 0
  const derivedPriceStr = currentProd ? String(currentProd.price_cents / 100).replace('.', ',') : ''

  // Ao apagar a ÚLTIMA variante (#479, critério 3) o produto volta a preço
  // simples: o campo volta a manual já com o último valor derivado.
  useEffect(() => {
    if (!hasVariants && currentProd) {
      setPriceStr(String(currentProd.price_cents / 100).replace('.', ','))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na transição
  }, [hasVariants])

  const mut = useMutation({
    mutationFn: () => {
      const price = parseCents(priceStr)
      const channels = {
        available_dine_in: availDineIn,
        available_takeaway: availTakeaway,
        available_delivery: availDelivery,
      }
      return currentProd
        ? prodApi.update(currentProd.id, {
            name,
            // #387: grava na linha lida (mesmo sem edição) — o servidor só
            // cria linha nova quando este locale não existe
            locale: sourceLocale,
            description: desc || null,
            // #479: com variantes o base é derivado — o preço manual NÃO é
            // enviado (a API ignoraria)
            ...(hasVariants ? {} : { price_cents: price }),
            category_id: catId,
            tax_category: taxCat,
            variant_group_label: variantLabel || null,
            // '' = principal
            kitchen_id: kitchenId || null,
            ...channels,
          })
        : prodApi.create({
            name,
            ...(desc ? { description: desc } : {}),
            // #403: produto novo nasce no CÓDIGO BASE (nada regional)
            locale: baseLocale,
            price_cents: price,
            // #428: moeda do PAÍS do tenant (cfg.currency); idioma é último recurso
            currency: currency || currencyFor(locale),
            category_id: catId,
            tax_category: taxCat,
            ...channels,
          })
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      if (!currentProd) {
        // Produto acabou de ser criado — fica aberto para adicionar ingredientes/extras
        setCurrentProd(data)
        toast.success(t('admin.menu.product_created'))
      } else {
        toast.success(t('admin.menu.product_updated'))
        if (data) setCurrentProd(data)
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const catOptions = categories.map((c) => ({
    value: c.id,
    label:
      displayTranslation(c.translations, lang, locale)?.name ?? c.translations[0]?.name ?? c.id,
  }))

  const taxOptions = [
    { value: 'food', label: t('admin.menu.tax_food') },
    { value: 'beverage', label: t('admin.menu.tax_beverage') },
    { value: 'tobacco', label: t('admin.menu.tax_tobacco') },
    { value: 'other', label: t('admin.menu.tax_other') },
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={prod ? t('admin.menu.edit_product') : t('admin.menu.new_product')}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            data-testid="catalog-product-close"
            onClick={onClose}
          >
            {t('admin.common.close')}
          </Button>
          <Button
            size="sm"
            loading={mut.isPending}
            data-testid="catalog-product-save"
            onClick={() => mut.mutate()}
          >
            {prod ? t('admin.common.save') : t('admin.menu.create_product')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* #387: produto só com traduções de outras línguas — nada pré-preenchido;
            role=status: leitores de tela anunciam quando aparece */}
        {prod && !source && (
          <p
            role="status"
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            data-testid="catalog-product-no-source"
          >
            {t('admin.menu.no_source_translation', { locale: languageLabel })}
          </p>
        )}
        {/* #403: indicador ÚNICO do idioma da linha carregada — vale para o
            par Nome/Descrição inteiro e mostra SÓ o código base (pt, en…) */}
        <p
          className="text-xs font-medium text-gray-500"
          data-testid="catalog-product-text-language"
        >
          {t('admin.menu.text_language', { locale: languageLabel })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('admin.menu.name')}
            data-testid="catalog-product-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Textarea
            label={t('admin.menu.description')}
            data-testid="catalog-product-description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={t('admin.menu.desc_ph')}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('admin.menu.price')}
            data-testid="catalog-product-price"
            value={hasVariants ? derivedPriceStr : priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            /* #479: derivado das variantes — readOnly (não disabled: segue
               legível e focável por teclado), valor vem do servidor */
            readOnly={hasVariants}
            aria-readonly={hasVariants}
            placeholder="29,90"
            hint={
              hasVariants
                ? t('admin.menu.price_from_variants_hint')
                : t('admin.menu.price_hint', { currency: currency || currencyFor(locale) })
            }
          />
          <Input
            label={t('admin.menu.variant_label')}
            data-testid="catalog-product-variant-label"
            value={variantLabel}
            onChange={(e) => setVariantLabel(e.target.value)}
            placeholder={t('admin.menu.variant_label_ph')}
            hint={t('admin.menu.variant_label_hint')}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label={t('admin.menu.category')}
            data-testid="catalog-product-category"
            value={catId}
            onChange={(e) => setCatId(e.target.value)}
            options={catOptions}
          />
          <Select
            label={t('admin.menu.tax_category')}
            data-testid="catalog-product-tax"
            value={taxCat}
            onChange={(e) => setTaxCat(e.target.value)}
            options={taxOptions}
          />
        </div>

        {kitchens.length > 0 && (
          <div>
            <Select
              label={t('admin.menu.kitchen')}
              data-testid="catalog-product-kitchen"
              value={kitchenId}
              onChange={(e) => setKitchenId(e.target.value)}
              options={[
                { value: '', label: t('admin.kitchens.main') },
                ...kitchens.map((k) => ({ value: k.id, label: k.name })),
              ]}
            />
            <p className="text-xs text-gray-500">{t('admin.menu.kitchen_hint')}</p>
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t('admin.menu.available_in')}</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="catalog-product-avail-dine_in"
                checked={availDineIn}
                onChange={(e) => setAvailDineIn(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-700">{t('admin.channel.dine_in')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="catalog-product-avail-takeaway"
                checked={availTakeaway}
                onChange={(e) => setAvailTakeaway(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-700">{t('admin.menu.takeaway')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="catalog-product-avail-delivery"
                checked={availDelivery}
                onChange={(e) => setAvailDelivery(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-700">{t('admin.menu.delivery')}</span>
            </label>
          </div>
        </div>

        {currentProd ? (
          <>
            <PromotionForm
              countryCode={countryCode ?? null}
              product={currentProd}
              locale={locale}
              currency={currency}
              priceLocale={priceLocale}
              timezone={timezone}
              qc={qc}
            />
            <VariantsEditor
              product={currentProd}
              locale={locale}
              currency={currency}
              priceLocale={priceLocale}
              qc={qc}
              onProductUpdate={setCurrentProd}
            />
            <IngredientsEditor product={currentProd} qc={qc} onProductUpdate={setCurrentProd} />
            <AllergensEditor product={currentProd} qc={qc} onProductUpdate={setCurrentProd} />
            <AttributesEditor product={currentProd} qc={qc} onProductUpdate={setCurrentProd} />
            <DailySpecialEditor
              product={currentProd}
              countryCode={countryCode ?? null}
              qc={qc}
              onProductUpdate={setCurrentProd}
            />
            <ExtrasEditor
              product={currentProd}
              locale={locale}
              currency={currency}
              priceLocale={priceLocale}
              qc={qc}
              onProductUpdate={setCurrentProd}
            />
            <ImagesEditor product={currentProd} qc={qc} onProductUpdate={setCurrentProd} />
          </>
        ) : (
          <p className="text-xs text-gray-400 text-center py-1">
            {t('admin.menu.create_product_first')}
          </p>
        )}
      </div>
    </Modal>
  )
}
